import { Badge } from "@dropbox/dig-components/badge";
import { Button } from "@dropbox/dig-components/buttons";
import { Spinner } from "@dropbox/dig-components/progress_indicators";
import { Text, Title } from "@dropbox/dig-components/typography";
import { Box, Split, Stack } from "@dropbox/dig-foundations";
import { SyncingLine } from "@dropbox/dig-icons/assets";
import { TrafficRoadBlockedSpot } from "@dropbox/dig-illustrations";
import {
  DashGlyph,
  DashWordmark,
  GlyphLogo,
  WordmarkLogo,
} from "@dropbox/dig-logos";
import { useQueryParams } from "@mirage/search/hooks/useQueryParams";
import { tagged } from "@mirage/service-logging";
import { completeJanusOauthConnection } from "@mirage/settings/hooks/connectors/useConnectConnector";
import i18n from "@mirage/translations";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { Suspense, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./AuthConfirmPage.module.css";

import { UIIcon } from "@dropbox/dig-icons";
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Dismiss_AddAppSuccessPage } from "@mirage/analytics/events/types/dismiss_add_app_success_page";
import { PAP_Shown_AddAppSuccessPage } from "@mirage/analytics/events/types/shown_add_app_success_page";
import { listConnections } from "@mirage/service-connectors";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { useDoOnceOnMount } from "@mirage/shared/hooks/useDoOnceOnMount";
import type { CompleteJanusOauthConnectionError } from "@mirage/shared/types";

const logger = tagged("AuthConfirmPage");

export function AuthConfirmPage() {
  const params = useQueryParams();
  const navigate = useNavigate();
  const request_id = params.get("request_id");
  const [response, setResponse] = useState<React.ReactNode>();
  const useUpdatedSuccessPage = useFeatureFlagValueBool(
    "dash_growth_2025_06_30_updated_auth_success_page",
  );

  const completeConnection = async () => {
    try {
      if (request_id) {
        const { connection_id, connector_id } =
          await completeJanusOauthConnection(request_id);
        const bc = new BroadcastChannel("janus_auth_channel");
        bc.postMessage({
          connection_id: connection_id,
          connector_id: connector_id,
        });

        if (!useUpdatedSuccessPage) {
          setResponse(
            <AuthConfirmationComponent
              illustration={<GlyphLogo size={80} src={DashGlyph} />}
              title={i18n.t("connector_connected_title")}
              description={i18n.t("connector_connected")}
            />,
          );
        } else {
          const connections = await listConnections();
          const connection = connections.find(
            (c) => c.id_attributes?.connector?.id === connector_id,
          );
          if (!connection || !connection.branding) {
            setResponse(
              <AuthConfirmationComponent
                illustration={<GlyphLogo size={80} src={DashGlyph} />}
                title={i18n.t("connector_connected_title")}
                description={i18n.t("connector_connected")}
              />,
            );
          } else {
            setResponse(
              <AuthConfirmationSuccessComponent
                iconSrc={connection?.branding?.icon_src || ""}
                appName={connection?.branding?.display_name || ""}
              />,
            );
          }
        }
        // Automatically close window after 2 seconds if connection was successful:
        // dash connectors page will automatically check for the updated connection
        setTimeout(
          () => {
            window.close();
          },
          useUpdatedSuccessPage ? 5000 : 2000,
        );
      } else {
        logger.error("No request_id found in authConfirmPage query params");
        navigate(RoutePath.ROOT);
      }
    } catch (e) {
      if (
        (e as CompleteJanusOauthConnectionError).reason ==
        "invalid_email_domain"
      ) {
        setResponse(
          <AuthConfirmationComponent
            illustration={<TrafficRoadBlockedSpot />}
            title={i18n.t(
              "failed_to_connect_due_to_invalid_email_domain_title",
            )}
            description={i18n.t(
              "failed_to_connect_due_to_invalid_email_domain",
            )}
          />,
        );
      } else {
        setResponse(
          <AuthConfirmationComponent
            illustration={<TrafficRoadBlockedSpot />}
            title={i18n.t("failed_to_connect_generic_title")}
            description={i18n.t("failed_to_connect_generic")}
          />,
        );
      }
    }
  };
  useEffect(() => {
    completeConnection();
  }, []);

  return (
    <div>
      <Stack padding="60">
        <Split alignX="center">
          <Split.Item>
            {response ? (
              response
            ) : (
              <Spinner
                aria-valuetext={i18n.t("connector_loading")}
                data-testid="loading-indicator"
              />
            )}
          </Split.Item>
        </Split>
      </Stack>
    </div>
  );
}

interface AuthConfirmationComponentProps {
  illustration: React.ReactNode;
  title: string;
  description: string;
}

const AuthConfirmationComponent = ({
  illustration,
  title,
  description,
}: AuthConfirmationComponentProps) => {
  const { reportPapEvent } = useMirageAnalyticsContext();

  useDoOnceOnMount(() => {
    reportPapEvent(
      PAP_Shown_AddAppSuccessPage({
        featureLine: "connector",
      }),
    );
  });

  const onDismiss = useCallback(() => {
    reportPapEvent(
      PAP_Dismiss_AddAppSuccessPage({
        featureLine: "connector",
      }),
    );
    window.close();
  }, [reportPapEvent]);

  return (
    <Suspense fallback={<></>}>
      <Stack display="flex" align="center" gap={"Macro XLarge"}>
        <Split alignY="center">
          <Split.Item>
            <WordmarkLogo
              src={DashWordmark}
              size={40}
              color="var(--dig-color__text__base)"
            />
          </Split.Item>
          <Split.Item>
            <Badge>Beta</Badge>
          </Split.Item>
        </Split>
        <div className={styles.illustrationContainer}>{illustration}</div>
        <Stack
          gap={"Micro Large"}
          display="flex"
          justify="center"
          align="center"
        >
          <Title size="medium" weightVariant="emphasized">
            {title}
          </Title>
          <Text size="xlarge" color="faint">
            {description}
          </Text>
        </Stack>
        <Button variant="filled" fullWidth size="xlarge" onClick={onDismiss}>
          {i18n.t("connector_close_window")}
        </Button>
      </Stack>
    </Suspense>
  );
};

interface AuthConfirmationSuccessComponentProps {
  appName: string;
  iconSrc: string;
}

const AuthConfirmationSuccessComponent = ({
  iconSrc = "",
  appName,
}: AuthConfirmationSuccessComponentProps) => {
  return (
    <Suspense fallback={<></>}>
      <Stack display="flex" align="center" gap={"Macro XLarge"}>
        <Box display="flex" flexDirection="row" columnGap="Macro Medium">
          <Box className={styles.dashGlyphContainer}>
            <GlyphLogo size={80} src={DashGlyph} />
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center">
            <Box
              className={styles.syncingLineContainer}
              display="flex"
              justifyContent="center"
              alignItems="center"
            >
              <UIIcon
                className={styles.syncingLine}
                src={SyncingLine}
                size="medium"
              />
            </Box>
          </Box>
          <Box
            className={styles.appGlyphContainer}
            display="flex"
            justifyContent="center"
            alignItems="center"
          >
            <img src={iconSrc} width="45px" height="45px" alt={appName} />
          </Box>
        </Box>
        <Stack
          gap={"Micro Large"}
          display="flex"
          justify="center"
          align="center"
        >
          <Title weightVariant="emphasized">
            {i18n.t("auth_confirm_success_title", { appName })}
          </Title>
          <Text size="large" color="faint">
            {i18n.t("auth_confirm_success_message")}
          </Text>
        </Stack>
        <Button
          className={styles.doneButton}
          variant="primary"
          size="xlarge"
          onClick={() => window.close()}
        >
          {i18n.t("done")}
        </Button>
      </Stack>
    </Suspense>
  );
};
